import type { Ref } from "vue";
import { ref } from "vue";
import { HttpClient } from "@/services/httpClient";
import { withFrappeLoading } from "@/services/frappe/list";
import { escapeFrappeLikeValue } from "@/services/frappe/filters";
import { Controllers, FrappeMethodType } from "@/type/api";
import { plainText } from "@/utils/richText";

const LOOKUP_PAGE_SIZE = 12;

export interface FrappeDoctypeLookupOption {
  id: string;
  label: string;
  subtitle?: string | null;
  image?: string | null;
  raw: Record<string, any>;
}

export interface SearchFrappeDoctypeParams {
  doctype: string;
  page?: number;
  search?: string;
  fields?: string[];
  searchFields?: string[];
  labelField?: string;
  subtitleFields?: string[];
  imageField?: string | null;
  limit?: number;
  orderBy?: string;
  baseFilters?: Array<[string, string, string, unknown]>;
}

export interface FrappeDoctypeLookupResult {
  items: FrappeDoctypeLookupOption[];
  hasMore: boolean;
}

function encodeDoctype(doctype: string) {
  return encodeURIComponent(doctype) as Controllers;
}

function uniqueFields(fields: Array<string | null | undefined>) {
  return [...new Set(fields.filter(Boolean) as string[])];
}

function pickStringValue(raw: Record<string, any>, fields: string[]) {
  for (const field of fields) {
    const value = raw?.[field];
    if (value == null)
      continue;

    const normalized = String(value).trim();
    if (normalized)
      return normalized;
  }

  return "";
}

export async function searchFrappeDoctype(
  loading: Ref<boolean>,
  {
    doctype,
    page = 1,
    search = "",
    fields = [],
    searchFields = [],
    labelField = "name",
    subtitleFields = [],
    imageField = null,
    limit = LOOKUP_PAGE_SIZE,
    orderBy = "modified desc",
    baseFilters = [],
  }: SearchFrappeDoctypeParams,
): Promise<FrappeDoctypeLookupResult> {
  return withFrappeLoading(loading, async () => {
    const service = new HttpClient(encodeDoctype(doctype));
    const resolvedSearchFields = searchFields.length ? searchFields : [labelField, "name"];
    const resolvedFields = uniqueFields([
      "name",
      labelField,
      ...fields,
      ...resolvedSearchFields,
      ...subtitleFields,
      imageField,
    ]);

    const filters = baseFilters.length ? JSON.stringify(baseFilters) : undefined;
    // Typed text matches literally — see `escapeFrappeLikeValue` for why `\`, `%`, `_` are escaped.
    const likeValue = escapeFrappeLikeValue(search.trim());
    const orFilters = likeValue
      ? JSON.stringify(
          resolvedSearchFields.map(field => [doctype, field, "like", `%${likeValue}%`]),
        )
      : undefined;

    const response = await service.get<{ data?: Record<string, any>[] }>(
      "",
      {
        limit_start: (page - 1) * limit,
        limit_page_length: limit,
        order_by: orderBy,
        ...(filters ? { filters } : {}),
        ...(orFilters ? { or_filters: orFilters } : {}),
      },
      {},
      JSON.stringify(resolvedFields),
      /**
       * CACHED, briefly.
       *
       * This is the read behind every autocomplete in the app, and the caching plan
       * names "search/select lookups refetch identical terms" as problem 2B. A user
       * typing, deleting and retyping the same term, or reopening the same picker,
       * asks the identical question repeatedly within seconds.
       *
       * The `operational` tier (45s) is deliberately short: a customer created a
       * minute ago must be findable. A write to the doctype clears it immediately
       * anyway — the tag comes from the URL, so no list is maintained by hand.
       *
       * ⚠️ Declared here rather than in the policy table because the URL cannot
       * tell this apart from a list page reading the same doctype.
       */
      { cachePolicy: { tier: "operational" } },
    );

    const rows = response?.data ?? [];

    return {
      items: rows.map((row) => {
        /**
         * ⚠️ `plainText` HERE IS NOT OPTIONAL, AND IT IS NOT A PRACTITIONER-ONLY CONCERN.
         *
         * `label` reaches `FrappeDoctypeAutocomplete` as `item-title`, which Vuetify renders in
         * the dropdown AND — once something is selected — inside the field's own `<input>`.
         * **Markup can never render in an input's value**, so a doctype whose display field
         * carries HTML (today `Healthcare Practitioner.practitioner_name`, which colour-codes
         * "Dr.") would show literal tags in every picker in the app.
         *
         * Styling the dropdown with `RichTextInline` instead was rejected: the list would render
         * styled and the selected value would stay raw, which is worse than being consistently
         * plain. **Pickers are a plain-text surface, by construction.**
         *
         * `raw` is deliberately left untouched — callers read the stored value from it.
         */
        const label = plainText(pickStringValue(row, [labelField, "item_name", "title", "name"]));
        const subtitle = plainText(pickStringValue(row, subtitleFields));

        return {
          id: String(row.name ?? row.id ?? label),
          label: label || String(row.name ?? ""),
          subtitle: subtitle || null,
          image: imageField ? (row?.[imageField] as string | null) ?? null : null,
          raw: row,
        };
      }),
      hasMore: rows.length === limit,
    };
  });
}

const LINK_SEARCH_LIMIT = 50;

// Frappe's own link search. Unlike a hand-written `like` over a guessed column list,
// this honours each doctype's configured `search_fields` (plus its `title_field` and
// name), so a doctype that is searchable in the Frappe desk is searchable here without
// this file having to know which columns that means.
//
// It answers with `{ message: [{ value, description }] }` where `value` is the docname —
// ids only, which is exactly what a link filter needs. It is NOT a substitute for a list
// query: no full rows, no total, no pagination.
async function searchLinkIds(doctype: string, search?: string | null): Promise<string[]> {
  const term = search?.trim();
  if (!term)
    return [];

  const service = new HttpClient(encodeDoctype(doctype));

  // `get()` always appends a `fields` param; search_link has no such argument and
  // ignores it, so the extra query key is harmless noise rather than a reason to
  // bypass the shared client (which carries auth, refresh and error handling).
  const response = await service.get<{ message?: Array<{ value?: string }> }>(
    "frappe.desk.search.search_link",
    {},
    {
      doctype,
      txt: term,
      page_length: LINK_SEARCH_LIMIT,
    },
    undefined,
    undefined,
    FrappeMethodType.method,
  );

  const rows = response?.message ?? (response as any)?.data?.message ?? [];

  return rows
    .map((row: { value?: string }) => String(row?.value ?? "").trim())
    .filter(Boolean);
}

// Link fields can't be matched with `like` against the linked doc's text columns in a
// list query, so searching by pet/owner name means resolving ids first and OR-ing them
// into the list filter. Frappe's search_link does the matching; the `like` sweep below
// is the fallback for when it is unavailable or the doctype exposes no search_fields.
async function lookupLinkIdsBySearch(
  doctype: string,
  search: string | null | undefined,
  fallback: SearchFrappeDoctypeParams,
): Promise<string[]> {
  const term = search?.trim();
  if (!term)
    return [];

  try {
    const ids = await searchLinkIds(doctype, term);
    if (ids.length)
      return ids;
  }
  catch (error) {
    console.warn(`search_link unavailable for ${doctype}, falling back to like search:`, error);
  }

  const result = await searchFrappeDoctype(ref(false), fallback);

  return result.items.map(item => item.id);
}

export async function lookupGuardianIdsBySearch(search?: string | null): Promise<string[]> {
  return lookupLinkIdsBySearch("Guardian", search, {
    doctype: "Guardian",
    search: search?.trim() ?? "",
    searchFields: ["full_name", "phone"],
    labelField: "full_name",
    fields: ["full_name", "phone"],
    limit: LINK_SEARCH_LIMIT,
    orderBy: "modified desc",
  });
}

export async function lookupPetIdsBySearch(search?: string | null): Promise<string[]> {
  return lookupLinkIdsBySearch("Pet", search, {
    doctype: "Pet",
    search: search?.trim() ?? "",
    searchFields: ["pet_name", "name"],
    labelField: "pet_name",
    fields: ["pet_name"],
    limit: LINK_SEARCH_LIMIT,
    orderBy: "modified desc",
  });
}
